import React,{useMemo,useState} from 'react';
import{BarChart,Bar,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,PieChart,Pie,Cell,Legend,LineChart,Line}from'recharts';
import{TrendingUp,TrendingDown,Wallet,FolderOpen,Filter,Download,X,FileText,Info}from'lucide-react';
import{useTheme,fmt,fmtDate,fmtDay,diffDays,COLORS,getMonthShort,PERIODS,getRange,getDayBounds,Card,StatCard,Btn,PageHeader,TabSwitch,Empty,DateField,downloadHtmlExcel,getLocale}from'./shared';
import{S}from'./strings';

function monthKey(date){
  const d=new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
}
function monthLabel(key){
  const[y,m]=key.split('-');
  return `${getMonthShort()[Number(m)-1]}. ${String(y).slice(2)}`;
}
function monthStart(key){
  const[y,m]=key.split('-').map(Number);
  return new Date(y,m-1,1,0,0,0,0);
}
function monthEnd(key){
  const[y,m]=key.split('-').map(Number);
  return new Date(y,m,0,23,59,59,999);
}
function buildMonthKeys(start,end){
  const from=new Date(start.getFullYear(),start.getMonth(),1);
  const to=new Date(end.getFullYear(),end.getMonth(),1);
  const keys=[];
  const cur=new Date(from);
  while(cur<=to){
    keys.push(`${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}`);
    cur.setMonth(cur.getMonth()+1);
  }
  return keys;
}

export default function ReportsTab({ data: rawData, uOrg, user, perms, aAccs: rawAccs, aFunds: rawFunds, isCompact, ...rest }) {
  // Stage 4.0 finishing (2026-05-12): операции со скрытых счетов теперь видны в отчётах.
  // Hidden — только про исключение счёта из total суммы. Для полной отчётности по
  // движениям денег бухгалтер/владелец должны видеть всё. Account-список остаётся
  // отфильтрованным (заначка не часть business reporting), но transactions — все.
  const data = {
    ...rawData,
    accounts: (rawData.accounts || []).filter(a => !a.isHidden),
    transactions: (rawData.transactions || []).filter(tx => !tx.archived)
  };

  // === НОВОЕ: Восстанавливаем aAccs и aFunds, чтобы код не выдавал ошибку ===
  const aAccs = (rawAccs || data.accounts.filter(a => !a.archived)).filter(a => !a.isHidden);
  const aFunds = rawFunds || data.funds.filter(f => !f.archived);
  const t=useTheme();
  const[view,setView]=useState('charts');
  const[period,setPeriod]=useState('month');
  const[exportType,setExportType]=useState('all');
  const[accFilter,setAccFilter]=useState('all');
  const[fundFilter,setFundFilter]=useState('all');
  const[dateFrom,setDateFrom]=useState('');
  const[dateTo,setDateTo]=useState('');
  const[contractorFilter,setContractorFilter]=useState('all'); // all | clients | suppliers

  const range=useMemo(()=>{
    if(dateFrom||dateTo){
      if(dateFrom&&!dateTo)return getDayBounds(dateFrom);
      return{start:dateFrom?getDayBounds(dateFrom).start:new Date('2000-01-01'),end:dateTo?getDayBounds(dateTo).end:new Date()};
    }
    return getRange(period);
  },[period,dateFrom,dateTo]);

  const {start,end}=range;
  const showPT=!(dateFrom||dateTo);

  const txMatchesFilters=(tx,{forBalance=false}={})=>{
    const d=new Date(tx.date);
    if(!forBalance&&(d<start||d>end))return false;
    if(accFilter!=='all'&&tx.accountId!==accFilter&&tx.toAccountId!==accFilter)return false;
    if(fundFilter!=='all'){
      const hasFund=tx.fundId===fundFilter||tx.toFundId===fundFilter||(tx.breakdown&&tx.breakdown.some(b=>b.fundId===fundFilter));
      if(!hasFund)return false;
    }
    return true;
  };

  const filteredTx=useMemo(()=>data.transactions.filter(tx=>txMatchesFilters(tx)),[data.transactions,start,end,accFilter,fundFilter]);
  const balanceRelevantTx=useMemo(()=>data.transactions.filter(tx=>txMatchesFilters(tx,{forBalance:true})),[data.transactions,accFilter,fundFilter]);

  const totals=useMemo(()=>({
    income:filteredTx.filter(x=>x.type==='income').reduce((s,x)=>s+x.amount,0),
    expense:filteredTx.filter(x=>x.type==='expense').reduce((s,x)=>s+x.amount,0),
    accounts:aAccs.reduce((s,a)=>s+(data.accountBalances[a.id]||0),0),
    funds:aFunds.reduce((s,f)=>s+(data.fundBalances[f.id]||0),0)
  }),[filteredTx,aAccs,aFunds,data.accountBalances,data.fundBalances]);

  const monthKeys=useMemo(()=>buildMonthKeys(start,end),[start,end]);

  const opuRows=useMemo(()=>{
    const months={};
    monthKeys.forEach(k=>{months[k]={income:0,expense:0,byAcc:{},byFund:{},byCat:{}};});
    filteredTx.forEach(tx=>{
      const mk=monthKey(tx.date);
      if(!months[mk])return;
      const bucket=months[mk];
      if(tx.type==='income'){
        bucket.income+=tx.amount;
        const an=data.accounts.find(a=>a.id===tx.accountId)?.name||'—';
        bucket.byAcc[an]=(bucket.byAcc[an]||0)+tx.amount;
      }else if(tx.type==='expense'){
        bucket.expense+=tx.amount;
        const fn=tx.fundName||S().reports.noFund;
        const cn=tx.expenseCategoryName||S().common.noCategoryShort;
        bucket.byFund[fn]=(bucket.byFund[fn]||0)+tx.amount;
        bucket.byCat[`${fn}|||${cn}`]=(bucket.byCat[`${fn}|||${cn}`]||0)+tx.amount;
      }
    });
    return months;
  },[filteredTx,monthKeys,data.accounts]);

  const incomeByAccount=useMemo(()=>{
    const m={};
    filteredTx.filter(x=>x.type==='income').forEach(tx=>{
      const an=data.accounts.find(a=>a.id===tx.accountId)?.name||'—';
      m[an]=(m[an]||0)+tx.amount;
    });
    return m;
  },[filteredTx,data.accounts]);

  const expenseByFundCategory=useMemo(()=>{
    const m={};
    filteredTx.filter(x=>x.type==='expense').forEach(tx=>{
      const fund=tx.fundName||S().reports.noFund;
      const cat=tx.expenseCategoryName||S().common.noCategoryShort;
      const color=data.funds.find(f=>f.name===fund)?.color||'';
      if(!m[fund])m[fund]={total:0,cats:{},color};
      m[fund].total+=tx.amount;
      m[fund].cats[cat]=(m[fund].cats[cat]||0)+tx.amount;
    });
    return m;
  },[filteredTx,data.funds]);

  // === НОВОЕ: Отчет по категориям (детальный) ===
  const categoriesData = useMemo(() => {
    const inc = {};
    const exp = {};
    filteredTx.forEach(tx => {
      if (tx.type === 'income') {
        const cat = tx.incomeCategoryName || S().common.noCategoryShort;
        inc[cat] = (inc[cat] || 0) + tx.amount;
      } else if (tx.type === 'expense') {
        const cat = tx.expenseCategoryName || S().common.noCategoryShort;
        exp[cat] = (exp[cat] || 0) + tx.amount;
      }
    });
    return { 
      income: Object.entries(inc).map(([name, value]) => ({ name, value })).sort((a,b) => b.value - a.value),
      expense: Object.entries(exp).map(([name, value]) => ({ name, value })).sort((a,b) => b.value - a.value)
    };
  }, [filteredTx]);

  // === НОВОЕ: Отчет по контрагентам (Обороты и Долги) ===
  const contractorAnalysis = useMemo(() => {
    const m = {};
    // 1. Обороты из транзакций
    filteredTx.forEach(tx => {
      const conId = tx.contractorId;
      if (!conId) return;
      const conName = tx.contractorName || data.contractors.find(c => c.id === conId)?.name || S().common.unknown;
      if (!m[conId]) m[conId] = { id: conId, name: conName, income: 0, expense: 0, debt: 0 };
      if (tx.type === 'income') m[conId].income += tx.amount;
      if (tx.type === 'expense') m[conId].expense += tx.amount;
    });
    // 2. Текущие долги — только активные и частично оплаченные.
    //    Зомби paid/0 и archived в outstanding не идут.
    (data.debts || []).forEach(d => {
      if (d.archived) return;
      if (d.status !== 'active' && d.status !== 'partial') return;
      const conId = d.contractorId;
      if (!conId) return;
      if (!m[conId]) {
        const conName = data.contractors.find(c => c.id === conId)?.name || S().common.unknown;
        m[conId] = { id: conId, name: conName, income: 0, expense: 0, debt: 0 };
      }
      const remain = d.amount - (d.paidAmount || 0);
      if (d.type === 'receivable') m[conId].debt += remain;
      else m[conId].debt -= remain;
    });
    // 3. Фильтр по роли (клиенты/поставщики)
    let arr = Object.values(m);
    if (contractorFilter !== 'all') {
      arr = arr.filter(row => {
        const c = data.contractors.find(x => x.id === row.id);
        if (!c) return false;
        if (contractorFilter === 'clients') return !!c.isCustomer;
        if (contractorFilter === 'suppliers') return !!c.isSupplier;
        return true;
      });
    }
    return arr.sort((a,b) => (b.income + b.expense) - (a.income + a.expense));
  }, [filteredTx, data.contractors, data.debts, contractorFilter]);

  // === НОВОЕ: Отчет по срокам (Пунктуальность - ТОЛЬКО входящие нам платежи) ===
  const timelinessAnalysis = useMemo(() => {
    const m = {};
    // Берем все долги типа receivable, которые были оплачены (status: paid или partial)
    (data.debts || []).filter(d => d.type === 'receivable').forEach(d => {
      const conId = d.contractorId;
      if (!conId) return;
      if (!m[conId]) m[conId] = { id: conId, name: data.contractors.find(c => c.id === conId)?.name || S().common.unknown, totalDebts: 0, totalDelay: 0, delayedCount: 0, onTimeCount: 0, history: [] };
      
      const deadline = new Date(d.date);
      const payments = d.payments || [];
      if (payments.length === 0) return; // Еще не платили

      m[conId].totalDebts++;
      // Берем последнюю дату платежа как финальную дату закрытия (или текущего прогресса)
      const lastPaymentDate = new Date(Math.max(...payments.map(p => new Date(p.date).getTime())));
      const delay = diffDays(lastPaymentDate, deadline);
      
      m[conId].totalDelay += delay;
      if (delay > 0) m[conId].delayedCount++;
      else m[conId].onTimeCount++;
      
      m[conId].history.push({ amount: d.amount, delay });
    });

    return Object.values(m)
      .filter(x => x.totalDebts > 0)
      .map(x => ({
        ...x,
        avgDelay: x.totalDelay / x.totalDebts,
        reliability: (x.onTimeCount / x.totalDebts) * 100
      }))
      .sort((a,b) => b.avgDelay - a.avgDelay);
  }, [data.debts, data.contractors]);

  // === НОВОЕ: Прогноз ликвидности (Деньги + Дебиторка - Кредиторка) ===
  const liquidityData = useMemo(() => {
    const cash = aAccs.reduce((s, a) => s + (data.accountBalances[a.id] || 0), 0);
    const receivables = (data.debts || []).filter(d => !d.archived && d.type === 'receivable' && (d.status === 'active' || d.status === 'partial'))
      .reduce((s, d) => s + (d.amount - (d.paidAmount || 0)), 0);
    const payables = (data.debts || []).filter(d => !d.archived && d.type === 'payable' && (d.status === 'active' || d.status === 'partial'))
      .reduce((s, d) => s + (d.amount - (d.paidAmount || 0)), 0);
    
    return { cash, receivables, payables, total: cash + receivables - payables };
  }, [aAccs, data.accountBalances, data.debts]);

  const ddsRows=useMemo(()=>{
    const rows={};
    monthKeys.forEach(k=>{rows[k]={opening:0,inflow:0,outflow:0,internal:0,net:0,closing:0};});

    monthKeys.forEach(k=>{
      const ms=monthStart(k);
      const me=monthEnd(k);

      let opening=0;
      balanceRelevantTx.forEach(tx=>{
        const d=new Date(tx.date);
        if(d>=ms)return;
        if(tx.type==='income')opening+=tx.amount;
        else if(tx.type==='expense')opening-=tx.amount;
        else if(tx.type==='transfer'&&tx.transferScope==='account'){
          if(accFilter==='all'){}else{
            if(tx.accountId===accFilter)opening-=tx.amount;
            if(tx.toAccountId===accFilter)opening+=tx.amount;
          }
        }
      });

      let inflow=0,outflow=0,internal=0;
      balanceRelevantTx.forEach(tx=>{
        const d=new Date(tx.date);
        if(d<ms||d>me)return;
        if(tx.type==='income')inflow+=tx.amount;
        else if(tx.type==='expense')outflow+=tx.amount;
        else if(tx.type==='transfer'&&tx.transferScope==='account'){
          if(accFilter==='all')internal+=tx.amount;
          else if(tx.accountId===accFilter||tx.toAccountId===accFilter)internal+=tx.amount;
        }
      });

      rows[k]={opening,inflow,outflow,internal,net:inflow-outflow,closing:opening+inflow-outflow};
    });

    return rows;
  },[monthKeys,balanceRelevantTx,accFilter]);

  const chartData=useMemo(()=>{
    const buckets=new Map();
    filteredTx.forEach(tx=>{
      const dt=new Date(tx.date);let key,label,sortTs;
      const sameDayOnly=dateFrom&&!dateTo;
      if(sameDayOnly||period==='day'){
        key=dt.toISOString().slice(0,13);
        sortTs=new Date(dt.getFullYear(),dt.getMonth(),dt.getDate(),dt.getHours()).getTime();
        label=`${String(dt.getHours()).padStart(2,'0')}:00`;
      }else if(period==='year'||period==='2year'||period==='all'){
        key=`${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`;
        sortTs=new Date(dt.getFullYear(),dt.getMonth(),1).getTime();
        label=dt.toLocaleDateString(getLocale(),{month:'short',year:'2-digit'});
      }else{
        key=dt.toISOString().slice(0,10);
        sortTs=new Date(dt.getFullYear(),dt.getMonth(),dt.getDate()).getTime();
        label=dt.toLocaleDateString(getLocale(),{day:'2-digit',month:'short'});
      }
      if(!buckets.has(key))buckets.set(key,{label,sortTs,income:0,expense:0,net:0});
      const b=buckets.get(key);
      if(tx.type==='income'){b.income+=tx.amount;b.net+=tx.amount;}
      if(tx.type==='expense'){b.expense+=tx.amount;b.net-=tx.amount;}
    });
    return [...buckets.values()].sort((a,b)=>a.sortTs-b.sortTs);
  },[filteredTx,period,dateFrom,dateTo]);

  const balanceLine=useMemo(()=>{
    let running=0;
    return chartData.map(row=>{running+=row.net;return{...row,balance:running};});
  },[chartData]);

  // === НОВОЕ: Данные для графика обязательств (Дебиторка и Кредиторка во времени) ===
  const obligationsChartData = useMemo(() => {
    const buckets = new Map();
    (data.debts || []).filter(d => d.status !== 'archived' && d.status !== 'paid').forEach(d => {
      const dt = new Date(d.date);
      const key = dt.toISOString().slice(0, 10);
      const sortTs = dt.getTime();
      const label = dt.toLocaleDateString(getLocale(), { day: '2-digit', month: 'short' });
      
      if (!buckets.has(key)) buckets.set(key, { label, sortTs, rec: 0, pay: 0 });
      const b = buckets.get(key);
      const rem = d.amount - (d.paidAmount || 0);
      if (d.type === 'receivable') b.rec += rem;
      else b.pay += rem;
    });
    return [...buckets.values()].sort((a,b) => a.sortTs - b.sortTs);
  }, [data.debts]);

  const expensePie=useMemo(()=>Object.entries(expenseByFundCategory).map(([name,{total,color}],i)=>({name,value:total,color:color||COLORS[i%COLORS.length]})),[expenseByFundCategory]);
  const incomePie=useMemo(()=>Object.entries(incomeByAccount).map(([name,value],i)=>({name,value,color:COLORS[i%COLORS.length]})),[incomeByAccount]);

  // Умный и детализированный экспорт в Excel
  const exportReport=()=>{
    const per=dateFrom||dateTo?`${dateFrom||'...'}–${dateTo||'...'}`:(period==='all'?'всё время':period);
    let h=`<h2>INMONEX — ${data.name}</h2><p>Период: ${per}</p>`;

    // СВОДКА
    if (exportType === 'all') {
      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#eef4ff"><th colspan="2">${S().reports.exportSummary}</th></tr>`;
      h+=`<tr><td>${S().reports.incomeLabel}</td><td align="right">${fmt(totals.income)}</td></tr>`;
      h+=`<tr><td>${S().reports.expenseLabel}</td><td align="right">${fmt(totals.expense)}</td></tr>`;
      h+=`<tr><td><b>${S().reports.opuTitle}: ${S().reports.opuProfit}</b></td><td align="right"><b>${fmt(totals.income-totals.expense)}</b></td></tr>`;
      h+=`<tr><td>${S().reports.onAccounts}</td><td align="right">${fmt(totals.accounts)}</td></tr>`;
      h+=`<tr><td>${S().reports.inFunds}</td><td align="right">${fmt(totals.funds)}</td></tr></table><br/>`;
    }

    // ДЕТАЛЬНЫЙ ОПиУ
    if (exportType === 'all' || exportType === 'opu') {
      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#fff3e0"><th>${S().reports.opuTitle} (${S().reports.opuArticle})</th>${monthKeys.map(k=>`<th>${monthLabel(k)}</th>`).join('')}<th>${S().reports.opuTotal}</th></tr>`;

      // Доходы с детализацией по счетам
      h+=`<tr style="background:#e8f5e9"><td><b>${S().reports.opuIncome}</b></td>${monthKeys.map(k=>`<td align="right"><b>${fmt(opuRows[k].income)}</b></td>`).join('')}<td align="right"><b>${fmt(totals.income)}</b></td></tr>`;
      Object.entries(incomeByAccount).forEach(([an, total]) => {
        h+=`<tr><td style="padding-left:15px; color:#444;">${an}</td>${monthKeys.map(k=>`<td align="right" style="color:#444;">${fmt(opuRows[k].byAcc[an]||0)}</td>`).join('')}<td align="right" style="color:#444;">${fmt(total)}</td></tr>`;
      });

      // Расходы с детализацией по фондам и категориям
      h+=`<tr style="background:#ffebee"><td><b>${S().reports.opuExpense}</b></td>${monthKeys.map(k=>`<td align="right"><b>${fmt(opuRows[k].expense)}</b></td>`).join('')}<td align="right"><b>${fmt(totals.expense)}</b></td></tr>`;
      Object.entries(expenseByFundCategory).forEach(([fund, { total, cats }]) => {
        h+=`<tr style="background:#fdfdfd"><td style="padding-left:15px;"><b>${fund}</b></td>${monthKeys.map(k=>`<td align="right"><b>${fmt(opuRows[k].byFund[fund]||0)}</b></td>`).join('')}<td align="right"><b>${fmt(total)}</b></td></tr>`;
        Object.entries(cats).forEach(([cat, catTotal]) => {
          h+=`<tr><td style="padding-left:30px; color:#666;">${cat}</td>${monthKeys.map(k=>`<td align="right" style="color:#666;">${fmt(opuRows[k].byCat[`${fund}|||${cat}`]||0)}</td>`).join('')}<td align="right" style="color:#666;">${fmt(catTotal)}</td></tr>`;
        });
      });

      // Прибыль / Убыток
      h+=`<tr style="background:#f3e5f5"><td><b>${S().reports.opuProfit}</b></td>${monthKeys.map(k=>`<td align="right"><b>${fmt(opuRows[k].income-opuRows[k].expense)}</b></td>`).join('')}<td align="right"><b>${fmt(totals.income-totals.expense)}</b></td></tr></table><br/>`;
    }

    // ТРАНЗАКЦИИ
    if (exportType === 'all') {
      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#e8f5e9"><th colspan="5">${S().reports.exportIncomes}</th></tr><tr><th>${S().tx.thDate}</th><th>${S().ops.account}</th><th>${S().tx.thAmount}</th><th>${S().tx.thComment}</th><th>${S().tx.thWho}</th></tr>`;
      filteredTx.filter(x=>x.type==='income').forEach(tx=>{h+=`<tr><td>${fmtDate(tx.date)}</td><td>${data.accounts.find(a=>a.id===tx.accountId)?.name||'—'}</td><td align="right">${fmt(tx.amount)}</td><td>${tx.comment||''}</td><td>${tx.userName||''}</td></tr>`;});
      h+=`</table><br/>`;

      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#ffebee"><th colspan="7">${S().reports.exportExpenses}</th></tr><tr><th>${S().tx.thDate}</th><th>${S().ops.account}</th><th>${S().tx.fund}</th><th>${S().tx.thCategory}</th><th>${S().tx.thAmount}</th><th>${S().tx.thComment}</th><th>${S().tx.thWho}</th></tr>`;
      filteredTx.filter(x=>x.type==='expense').forEach(tx=>{h+=`<tr><td>${fmtDate(tx.date)}</td><td>${data.accounts.find(a=>a.id===tx.accountId)?.name||'—'}</td><td>${tx.fundName||'—'}</td><td>${tx.expenseCategoryName||'—'}</td><td align="right">${fmt(tx.amount)}</td><td>${tx.comment||''}</td><td>${tx.userName||''}</td></tr>`;});
      h+=`</table><br/>`;

      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#f3e5f5"><th colspan="7">${S().reports.exportTransfers}</th></tr><tr><th>${S().tx.thDate}</th><th>${S().tx.thType}</th><th>${S().ops.fromAccount}</th><th>${S().ops.toAccount}</th><th>${S().tx.thAmount}</th><th>${S().tx.thComment}</th><th>${S().tx.thWho}</th></tr>`;
      filteredTx.filter(x=>x.type==='transfer').forEach(tx=>{h+=`<tr><td>${fmtDate(tx.date)}</td><td>${tx.transferScope==='fund'?S().ops.transferBetweenFunds:S().ops.transferBetweenAccounts}</td><td>${tx.transferScope==='fund'?(tx.fundName||'—'):(data.accounts.find(a=>a.id===tx.accountId)?.name||'—')}</td><td>${tx.transferScope==='fund'?(tx.toFundName||'—'):(data.accounts.find(a=>a.id===tx.toAccountId)?.name||'—')}</td><td align="right">${fmt(tx.amount)}</td><td>${tx.comment||''}</td><td>${tx.userName||''}</td></tr>`;});
      h+=`</table><br/>`;
    }

    // ДДС
    if (exportType === 'all' || exportType === 'dds') {
      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#e3f2fd"><th>${S().reports.ddsTitle} (${S().reports.opuArticle})</th>${monthKeys.map(k=>`<th>${monthLabel(k)}</th>`).join('')}<th>${S().reports.opuTotal}</th></tr>`;
      const ddsTotal=(field)=>monthKeys.reduce((s,k)=>s+(ddsRows[k]?.[field]||0),0);

      h+=`<tr><td><b>${S().reports.ddsOpening}</b></td>${monthKeys.map(k=>`<td align="right">${fmt(ddsRows[k].opening)}</td>`).join('')}<td align="right">—</td></tr>`;
      h+=`<tr style="background:#e8f5e9"><td><b>${S().reports.ddsInflow}</b></td>${monthKeys.map(k=>`<td align="right">${fmt(ddsRows[k].inflow)}</td>`).join('')}<td align="right"><b>${fmt(ddsTotal('inflow'))}</b></td></tr>`;
      h+=`<tr style="background:#ffebee"><td><b>${S().reports.ddsOutflow}</b></td>${monthKeys.map(k=>`<td align="right">${fmt(ddsRows[k].outflow)}</td>`).join('')}<td align="right"><b>${fmt(ddsTotal('outflow'))}</b></td></tr>`;
      h+=`<tr><td>${S().reports.ddsInternal}</td>${monthKeys.map(k=>`<td align="right">${fmt(ddsRows[k].internal)}</td>`).join('')}<td align="right">${fmt(ddsTotal('internal'))}</td></tr>`;
      h+=`<tr style="background:#f3e5f5"><td><b>${S().reports.ddsNetFlow}</b></td>${monthKeys.map(k=>`<td align="right"><b>${fmt(ddsRows[k].net)}</b></td>`).join('')}<td align="right"><b>${fmt(ddsTotal('net'))}</b></td></tr>`;
      h+=`<tr><td><b>${S().reports.ddsClosing}</b></td>${monthKeys.map(k=>`<td align="right">${fmt(ddsRows[k].closing)}</td>`).join('')}<td align="right"><b>${fmt(ddsRows[monthKeys[monthKeys.length-1]]?.closing||0)}</b></td></tr></table><br/>`;
    }

    // КАТЕГОРИИ
    if (exportType === 'all' || exportType === 'categories') {
      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#e8f5e9"><th colspan="3">${S().reports.exportIncomeByCat}</th></tr><tr><th>${S().reports.exportCategoryCol}</th><th>${S().reports.exportAmountCol}</th><th>%</th></tr>`;
      categoriesData.income.forEach(c=>{h+=`<tr><td>${c.name}</td><td align="right">${fmt(c.value)}</td><td align="right">${(totals.income > 0 ? ((c.value/totals.income)*100).toFixed(1) : '0.0')}%</td></tr>`;});
      h+=`</table><br/>`;
      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#ffebee"><th colspan="3">${S().reports.exportExpenseByCat}</th></tr><tr><th>${S().reports.exportCategoryCol}</th><th>${S().reports.exportAmountCol}</th><th>%</th></tr>`;
      categoriesData.expense.forEach(c=>{h+=`<tr><td>${c.name}</td><td align="right">${fmt(c.value)}</td><td align="right">${(totals.expense > 0 ? ((c.value/totals.expense)*100).toFixed(1) : '0.0')}%</td></tr>`;});
      h+=`</table><br/>`;
    }

    // КОНТРАГЕНТЫ
    if (exportType === 'all' || exportType === 'contractors') {
      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#eef4ff"><th colspan="5">${S().reports.exportContractorActivity}</th></tr><tr><th>${S().reports.exportContractorCol}</th><th>${S().reports.exportRevenueCol}</th><th>${S().reports.exportCostCol}</th><th>${S().reports.exportBalanceCol}</th><th>${S().reports.exportDebtBalanceCol}</th></tr>`;
      contractorAnalysis.forEach(c=>{h+=`<tr><td>${c.name}</td><td align="right">${fmt(c.income)}</td><td align="right">${fmt(c.expense)}</td><td align="right">${fmt(c.income-c.expense)}</td><td align="right">${fmt(c.debt)}</td></tr>`;});
      h+=`</table><br/>`;
    }

    // ПУНКТУАЛЬНОСТЬ
    if (exportType === 'all' || exportType === 'timeliness') {
      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#e0f2f1"><th colspan="4">${S().reports.exportTimeliness}</th></tr><tr><th>${S().reports.exportClientCol}</th><th>${S().reports.exportDealsCol}</th><th>${S().reports.exportPunctualityCol}</th><th>${S().reports.exportAvgDelayCol}</th></tr>`;
      timelinessAnalysis.forEach(c=>{h+=`<tr><td>${c.name}</td><td align="right">${c.totalDebts}</td><td align="right">${c.reliability.toFixed(0)}%</td><td align="right">${c.avgDelay.toFixed(1)} ${S().reports.exportDaysShort}</td></tr>`;});
      h+=`</table><br/>`;
    }

    // ЛИКВИДНОСТЬ
    if (exportType === 'all' || exportType === 'liquidity') {
      h+=`<table border="1" cellspacing="0" cellpadding="5"><tr style="background:#e3f2fd"><th colspan="2">${S().reports.exportLiquidityPosition}</th></tr>`;
      h+=`<tr><td>${S().reports.exportCashOnAccounts}</td><td align="right">${fmt(liquidityData.cash)}</td></tr>`;
      h+=`<tr><td>${S().reports.exportReceivablesPlus}</td><td align="right">${fmt(liquidityData.receivables)}</td></tr>`;
      h+=`<tr><td>${S().reports.exportPayablesMinus}</td><td align="right">${fmt(liquidityData.payables)}</td></tr>`;
      h+=`<tr style="background:#bbdefb"><td><b>${S().reports.exportTotalLiquidity}</b></td><td align="right"><b>${fmt(liquidityData.total)}</b></td></tr></table><br/>`;
    }

    const reportNames = S().reports.exportReportName;
    downloadHtmlExcel(`inmonex-report-${exportType}-${data.name}-${fmtDay(new Date())}.xls`, `${S().reports.reportLabel} ${reportNames[exportType] || exportType}`, [h]);
  };

  const tooltipStyle={background:t.bgCard,border:`1px solid ${t.border}`,borderRadius:10,fontSize:13,color:t.text};
  const yF=v=>v>=1e6?`${(v/1e6).toFixed(1)}M`:v>=1e3?`${(v/1e3).toFixed(0)}k`:v;
  const hasFilter=accFilter!=='all'||fundFilter!=='all'||dateFrom||dateTo;
  const cols=isCompact?'1fr':'1fr 1fr';
  const scols=isCompact?'1fr 1fr':'repeat(4,1fr)';
  const ddsSummary={
    opening:ddsRows[monthKeys[0]]?.opening||0,
    inflow:monthKeys.reduce((s,k)=>s+(ddsRows[k]?.inflow||0),0),
    outflow:monthKeys.reduce((s,k)=>s+(ddsRows[k]?.outflow||0),0),
    internal:monthKeys.reduce((s,k)=>s+(ddsRows[k]?.internal||0),0),
    closing:ddsRows[monthKeys[monthKeys.length-1]]?.closing||0
  };

  return<div>
    <PageHeader 
      title={S().reports.title} 
      right={
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
          {showPT&&<TabSwitch tabs={PERIODS} active={period} onChange={setPeriod}/>}
          
          <div style={{display:'flex', alignItems:'center', background:t.bgInput, borderRadius:10, border:`1px solid ${t.border}`, overflow:'hidden'}}>
            <select 
              value={exportType} 
              onChange={e=>setExportType(e.target.value)} 
              style={{border:'none', background:'transparent', padding:'8px 10px', fontSize:13, color:t.text, outline:'none', cursor:'pointer'}}
            >
              <option value="all">{S().reports.allReport}</option>
              <option value="opu">{S().reports.onlyOpu}</option>
              <option value="dds">{S().reports.onlyDds}</option>
              <option value="categories">{S().reports.onlyCategoriesExport}</option>
              <option value="contractors">{S().reports.onlyContractorsExport}</option>
              <option value="timeliness">{S().reports.onlyTimelinessExport}</option>
              <option value="liquidity">{S().reports.onlyLiquidityExport}</option>
            </select>
            <button 
              onClick={exportReport} 
              style={{display:'flex', alignItems:'center', gap:6, padding:'8px 14px', background:t.accent, color:t.btnText, fontWeight:600, fontSize:13, cursor:'pointer', border:'none'}}
            >
              <Download size={14}/> {S().common.download}
            </button>
          </div>
        </div>
      }
    />

    <div style={{display:'flex',gap:6,marginBottom:18, overflowX:'auto', paddingBottom:4}} className="hide-scroll">
      <TabSwitch tabs={[
        {id:'charts',label:S().reports.charts},
        {id:'opu',label:S().reports.opu,icon:FileText},
        {id:'dds',label:S().reports.dds,icon:FileText},
        {id:'categories',label:S().reports.categoriesTab},
        {id:'contractors',label:S().reports.contractorsTab},
        {id:'timeliness',label:S().reports.timelinessTab},
        {id:'liquidity',label:S().reports.liquidityTab}
      ]} active={view} onChange={setView}/>
    </div>

    <div style={{display:'flex',gap:10,marginBottom:20,alignItems:'center',flexWrap:'wrap'}}>
      <Filter size={14} color={hasFilter?t.accent:t.textMuted}/>
      <select value={accFilter} onChange={e=>setAccFilter(e.target.value)} style={{width:'auto',...(accFilter!=='all'?{background:t.activeFilter,borderColor:t.accent}:{})}}><option value="all">{S().reports.allAccounts}</option>{aAccs.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}</select>
      <select value={fundFilter} onChange={e=>setFundFilter(e.target.value)} style={{width:'auto',...(fundFilter!=='all'?{background:t.activeFilter,borderColor:t.accent}:{})}}><option value="all">{S().reports.allFunds}</option>{aFunds.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}</select>
      <DateField value={dateFrom} onChange={setDateFrom} placeholder={S().reports.dateFrom} width={isCompact?130:160} active={!!dateFrom}/>
      <DateField value={dateTo} onChange={setDateTo} placeholder={S().reports.dateTo} width={isCompact?130:160} active={!!dateTo}/>
      {hasFilter&&<button onClick={()=>{setAccFilter('all');setFundFilter('all');setDateFrom('');setDateTo('');}} style={{background:`${t.accentRed}14`,color:t.accentRed,padding:'5px 10px',borderRadius:8,fontSize:12,fontWeight:600}}><X size={12}/> {S().reports.resetFilters}</button>}
    </div>

    <div style={{display:'grid',gridTemplateColumns:scols,gap: isCompact ? 8 : 16,marginBottom:24}}>
      <StatCard icon={TrendingUp} label={S().reports.incomeLabel} value={fmt(totals.income)} color={t.accentGreen} compact={isCompact}/>
      <StatCard icon={TrendingDown} label={S().reports.expenseLabel} value={fmt(totals.expense)} color={t.accentRed} compact={isCompact}/>
      <StatCard icon={Wallet} label={S().reports.onAccounts} value={fmt(totals.accounts)} color={t.accent} compact={isCompact}/>
      <StatCard icon={FolderOpen} label={S().reports.inFunds} value={fmt(totals.funds)} color={t.accentPurple} compact={isCompact}/>
    </div>

    {view==='charts'&&<>
      <div style={{display:'grid',gridTemplateColumns:cols,gap:18,marginBottom:18}}>
        <Card><div style={{fontSize:15,fontWeight:700,marginBottom:14}}>{S().reports.chartIncomeExpense}</div>{chartData.length>0?<ResponsiveContainer width="100%" height={240}><BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke={t.border}/><XAxis dataKey="label" stroke={t.textMuted} fontSize={11}/><YAxis stroke={t.textMuted} fontSize={10} tickFormatter={yF}/><Tooltip contentStyle={tooltipStyle} formatter={v=>fmt(v)}/><Bar dataKey="income" fill={t.accentGreen} name={S().reports.incomeLabel} radius={[4,4,0,0]}/><Bar dataKey="expense" fill={t.accentRed} name={S().reports.expenseLabel} radius={[4,4,0,0]}/></BarChart></ResponsiveContainer>:<Empty/>}</Card>
        <Card><div style={{fontSize:15,fontWeight:700,marginBottom:14}}>{S().reports.chartExpenseByFunds}</div>{expensePie.length>0?<ResponsiveContainer width="100%" height={240}><PieChart><Pie data={expensePie} cx="50%" cy="50%" outerRadius={82} innerRadius={44} dataKey="value" paddingAngle={2} stroke={t.bgCard} strokeWidth={2}>{expensePie.map((d,i)=><Cell key={i} fill={d.color}/>)}</Pie><Tooltip contentStyle={tooltipStyle} formatter={v=>fmt(v)}/><Legend wrapperStyle={{fontSize:11}}/></PieChart></ResponsiveContainer>:<Empty/>}</Card>
      </div>
      <div style={{display:'grid',gridTemplateColumns:cols,gap:18}}>
        <Card><div style={{fontSize:15,fontWeight:700,marginBottom:14}}>{S().reports.chartIncomeByAccounts}</div>{incomePie.length>0?<ResponsiveContainer width="100%" height={240}><PieChart><Pie data={incomePie} cx="50%" cy="50%" outerRadius={82} innerRadius={44} dataKey="value" paddingAngle={2} stroke={t.bgCard} strokeWidth={2}>{incomePie.map((d,i)=><Cell key={i} fill={d.color}/>)}</Pie><Tooltip contentStyle={tooltipStyle} formatter={v=>fmt(v)}/><Legend wrapperStyle={{fontSize:11}}/></PieChart></ResponsiveContainer>:<Empty/>}</Card>
        <Card><div style={{fontSize:15,fontWeight:700,marginBottom:14}}>{S().reports.chartCumulativeResult}</div>{balanceLine.length>0?<ResponsiveContainer width="100%" height={240}><LineChart data={balanceLine}><CartesianGrid strokeDasharray="3 3" stroke={t.border}/><XAxis dataKey="label" stroke={t.textMuted} fontSize={11}/><YAxis stroke={t.textMuted} fontSize={10} tickFormatter={yF}/><Tooltip contentStyle={tooltipStyle} formatter={v=>fmt(v)}/><Line type="monotone" dataKey="balance" stroke={t.accent} strokeWidth={2.5} dot={{r:2}} name={S().reports.resultChartName}/></LineChart></ResponsiveContainer>:<Empty/>}</Card>
      </div>
      <div style={{marginTop:18}}>
        <Card>
          <div style={{fontSize:15,fontWeight:700,marginBottom:14}}>{S().reports.obligationsCalendar}</div>
          {obligationsChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={obligationsChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={t.border}/>
                <XAxis dataKey="label" stroke={t.textMuted} fontSize={11}/>
                <YAxis stroke={t.textMuted} fontSize={10} tickFormatter={yF}/>
                <Tooltip contentStyle={tooltipStyle} formatter={v=>fmt(v)}/>
                <Legend wrapperStyle={{fontSize:12}} />
                <Line type="monotone" dataKey="rec" stroke={t.accentGreen} strokeWidth={2.5} dot={{r:4}} name={S().reports.weWillReceive}/>
                <Line type="monotone" dataKey="pay" stroke={t.accentRed} strokeWidth={2.5} dot={{r:4}} name={S().reports.weWillPay}/>
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty text={S().reports.noUpcomingPayments}/>}
        </Card>
      </div>
    </>}

    {view==='opu'&&<Card style={{overflow:'auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',gap:12,alignItems:'flex-start',marginBottom:16,flexWrap:'wrap'}}>
        <div>
          <div style={{fontSize:16,fontWeight:800}}>{S().reports.opuTitle}</div>
          <div style={{fontSize:12,color:t.textMuted,marginTop:4}}>{S().reports.opuDescription}</div>
        </div>
        <div style={{padding:'10px 12px',borderRadius:12,background:t.bgInput,border:`1px solid ${t.border}`,minWidth:220}}>
          <div style={{fontSize:12,color:t.textMuted}}>{S().reports.opuPeriodResult}</div>
          <div className="mono" style={{fontSize:20,fontWeight:800,color:(totals.income-totals.expense)>=0?t.accentGreen:t.accentRed}}>{fmt(totals.income-totals.expense)}</div>
        </div>
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead><tr style={{background:t.bgInput}}><th style={{padding:'8px 12px',textAlign:'left',fontWeight:700,minWidth: isCompact ? 120 : 220}}>{S().reports.opuArticle}</th>{monthKeys.map(k=><th key={k} style={{padding:'8px 10px',textAlign:'right',fontWeight:600,minWidth: isCompact ? 60 : 90}}>{monthLabel(k)}</th>)}<th style={{padding:'8px 12px',textAlign:'right',fontWeight:700,minWidth:110}}>{S().reports.opuTotal}</th></tr></thead>
        <tbody>
          <tr style={{background:`${t.accentGreen}10`}}><td style={{padding:'8px 12px',fontWeight:700,color:t.accentGreen}}>{S().reports.opuIncome}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'8px 10px',textAlign:'right',fontWeight:700}}>{fmt(opuRows[k].income)}</td>)}<td className="mono" style={{padding:'8px 12px',textAlign:'right',fontWeight:800}}>{fmt(totals.income)}</td></tr>
          {Object.entries(incomeByAccount).map(([an,total])=><tr key={an} style={{borderBottom:`1px solid ${t.border}`}}><td style={{padding:'6px 12px 6px 24px',color:t.textSec}}>{an}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'6px 10px',textAlign:'right',color:t.textSec}}>{fmt(opuRows[k].byAcc[an]||0)}</td>)}<td className="mono" style={{padding:'6px 12px',textAlign:'right'}}>{fmt(total)}</td></tr>)}
          <tr style={{background:`${t.accentRed}10`}}><td style={{padding:'8px 12px',fontWeight:700,color:t.accentRed}}>{S().reports.opuExpense}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'8px 10px',textAlign:'right',fontWeight:700}}>{fmt(opuRows[k].expense)}</td>)}<td className="mono" style={{padding:'8px 12px',textAlign:'right',fontWeight:800}}>{fmt(totals.expense)}</td></tr>
          {Object.entries(expenseByFundCategory).map(([fund,{total,cats,color}])=><React.Fragment key={fund}><tr style={{background:`${color||t.border}08`,borderTop:`2px solid ${color||t.border}`}}><td style={{padding:'7px 12px 7px 20px',fontWeight:700}}>{fund}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'7px 10px',textAlign:'right',fontWeight:600}}>{fmt(opuRows[k].byFund[fund]||0)}</td>)}<td className="mono" style={{padding:'7px 12px',textAlign:'right',fontWeight:700}}>{fmt(total)}</td></tr>{Object.entries(cats).map(([cat,catTotal])=><tr key={cat} style={{borderBottom:`1px solid ${t.border}`}}><td style={{padding:'5px 12px 5px 40px',color:t.textSec}}>{cat}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'5px 10px',textAlign:'right',color:t.textSec}}>{fmt(opuRows[k].byCat[`${fund}|||${cat}`]||0)}</td>)}<td className="mono" style={{padding:'5px 12px',textAlign:'right'}}>{fmt(catTotal)}</td></tr>)}</React.Fragment>)}
          <tr style={{background:`${t.accent}10`,borderTop:`2px solid ${t.accent}`}}><td style={{padding:'10px 12px',fontWeight:800}}>{S().reports.opuProfit}</td>{monthKeys.map(k=>{const v=opuRows[k].income-opuRows[k].expense;return<td key={k} className="mono" style={{padding:'10px 10px',textAlign:'right',fontWeight:800,color:v>=0?t.accentGreen:t.accentRed}}>{fmt(v)}</td>;})}<td className="mono" style={{padding:'10px 12px',textAlign:'right',fontWeight:900,color:(totals.income-totals.expense)>=0?t.accentGreen:t.accentRed}}>{fmt(totals.income-totals.expense)}</td></tr>
        </tbody>
      </table>
    </Card>}

    {view==='dds'&&<Card style={{overflow:'auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',gap:12,alignItems:'flex-start',marginBottom:16,flexWrap:'wrap'}}>
        <div>
          <div style={{fontSize:16,fontWeight:800}}>{S().reports.ddsTitle}</div>
          <div style={{fontSize:12,color:t.textMuted,marginTop:4}}>{S().reports.ddsDescription}</div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:isCompact?'1fr':'repeat(3,minmax(160px,1fr))',gap:10,minWidth:isCompact?0:520}}>
          <div style={{padding:'10px 12px',borderRadius:12,background:t.bgInput,border:`1px solid ${t.border}`}}><div style={{fontSize:12,color:t.textMuted}}>{S().reports.ddsOpening}</div><div className="mono" style={{fontSize:18,fontWeight:800}}>{fmt(ddsSummary.opening)}</div></div>
          <div style={{padding:'10px 12px',borderRadius:12,background:t.bgInput,border:`1px solid ${t.border}`}}><div style={{fontSize:12,color:t.textMuted}}>{S().reports.ddsNetFlowLabel}</div><div className="mono" style={{fontSize:18,fontWeight:800,color:ddsSummary.inflow-ddsSummary.outflow>=0?t.accentGreen:t.accentRed}}>{fmt(ddsSummary.inflow-ddsSummary.outflow)}</div></div>
          <div style={{padding:'10px 12px',borderRadius:12,background:t.bgInput,border:`1px solid ${t.border}`}}><div style={{fontSize:12,color:t.textMuted}}>{S().reports.ddsClosing}</div><div className="mono" style={{fontSize:18,fontWeight:800}}>{fmt(ddsSummary.closing)}</div></div>
        </div>
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead><tr style={{background:t.bgInput}}><th style={{padding:'8px 12px',textAlign:'left',fontWeight:700,minWidth: isCompact ? 120 : 220}}>{S().reports.opuArticle}</th>{monthKeys.map(k=><th key={k} style={{padding:'8px 10px',textAlign:'right',fontWeight:600,minWidth: isCompact ? 60 : 90}}>{monthLabel(k)}</th>)}<th style={{padding:'8px 12px',textAlign:'right',fontWeight:700,minWidth:110}}>{S().reports.opuTotal}</th></tr></thead>
        <tbody>
          <tr><td style={{padding:'8px 12px',fontWeight:700}}>{S().reports.ddsOpening}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'8px 10px',textAlign:'right'}}>{fmt(ddsRows[k].opening)}</td>)}<td className="mono" style={{padding:'8px 12px',textAlign:'right'}}>—</td></tr>
          <tr style={{background:`${t.accentGreen}10`}}><td style={{padding:'8px 12px',fontWeight:700,color:t.accentGreen}}>{S().reports.ddsInflow}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'8px 10px',textAlign:'right'}}>{fmt(ddsRows[k].inflow)}</td>)}<td className="mono" style={{padding:'8px 12px',textAlign:'right',fontWeight:800}}>{fmt(ddsSummary.inflow)}</td></tr>
          <tr style={{background:`${t.accentRed}10`}}><td style={{padding:'8px 12px',fontWeight:700,color:t.accentRed}}>{S().reports.ddsOutflow}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'8px 10px',textAlign:'right'}}>{fmt(ddsRows[k].outflow)}</td>)}<td className="mono" style={{padding:'8px 12px',textAlign:'right',fontWeight:800}}>{fmt(ddsSummary.outflow)}</td></tr>
          <tr><td style={{padding:'8px 12px',fontWeight:700}}>{S().reports.ddsInternal}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'8px 10px',textAlign:'right',color:t.textSec}}>{fmt(ddsRows[k].internal)}</td>)}<td className="mono" style={{padding:'8px 12px',textAlign:'right'}}>{fmt(ddsSummary.internal)}</td></tr>
          <tr style={{background:`${t.accent}10`,borderTop:`2px solid ${t.accent}`}}><td style={{padding:'10px 12px',fontWeight:800}}>{S().reports.ddsNetFlow}</td>{monthKeys.map(k=>{const v=ddsRows[k].net;return<td key={k} className="mono" style={{padding:'10px 10px',textAlign:'right',fontWeight:800,color:v>=0?t.accentGreen:t.accentRed}}>{fmt(v)}</td>;})}<td className="mono" style={{padding:'10px 12px',textAlign:'right',fontWeight:900,color:(ddsSummary.inflow-ddsSummary.outflow)>=0?t.accentGreen:t.accentRed}}>{fmt(ddsSummary.inflow-ddsSummary.outflow)}</td></tr>
          <tr><td style={{padding:'8px 12px',fontWeight:700}}>{S().reports.ddsClosing}</td>{monthKeys.map(k=><td key={k} className="mono" style={{padding:'8px 10px',textAlign:'right'}}>{fmt(ddsRows[k].closing)}</td>)}<td className="mono" style={{padding:'8px 12px',textAlign:'right',fontWeight:800}}>{fmt(ddsSummary.closing)}</td></tr>
        </tbody>
      </table>
      <div style={{display:'flex',alignItems:'center',gap:8,marginTop:14,fontSize:12,color:t.textMuted}}><Info size={14}/>{S().reports.ddsFilterHint}</div>
    </Card>}

    {view==='categories'&&<div style={{display:'grid', gridTemplateColumns:cols, gap:18}}>
      <Card>
        <div style={{fontSize:16, fontWeight:800, marginBottom:16, color:t.accentGreen}}>{S().reports.incomeByCategories}</div>
        <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
          <thead><tr style={{textAlign:'left', color:t.textMuted, borderBottom:`1px solid ${t.border}`}}><th style={{padding:8}}>{S().reports.categoryColumn}</th><th style={{padding:8, textAlign:'right'}}>{S().reports.amountColumn}</th><th style={{padding:8, textAlign:'right'}}>%</th></tr></thead>
          <tbody>
            {categoriesData.income.map(c => (
              <tr key={c.name} style={{borderBottom:`1px solid ${t.border}`}}>
                <td style={{padding:8}}>{c.name}</td>
                <td style={{padding:8, textAlign:'right', fontWeight:700}}>{fmt(c.value)}</td>
                <td style={{padding:8, textAlign:'right', color:t.textMuted}}>{(totals.income > 0 ? ((c.value/totals.income)*100).toFixed(1) : '0.0')}%</td>
              </tr>
            ))}
            {categoriesData.income.length===0&&<tr><td colSpan="3" style={{textAlign:'center', padding:20, color:t.textMuted}}>{S().common.noData}</td></tr>}
          </tbody>
        </table>
      </Card>
      <Card>
        <div style={{fontSize:16, fontWeight:800, marginBottom:16, color:t.accentRed}}>{S().reports.expenseByCategories}</div>
        <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
          <thead><tr style={{textAlign:'left', color:t.textMuted, borderBottom:`1px solid ${t.border}`}}><th style={{padding:8}}>{S().reports.categoryColumn}</th><th style={{padding:8, textAlign:'right'}}>{S().reports.amountColumn}</th><th style={{padding:8, textAlign:'right'}}>%</th></tr></thead>
          <tbody>
            {categoriesData.expense.map(c => (
              <tr key={c.name} style={{borderBottom:`1px solid ${t.border}`}}>
                <td style={{padding:8}}>{c.name}</td>
                <td style={{padding:8, textAlign:'right', fontWeight:700}}>{fmt(c.value)}</td>
                <td style={{padding:8, textAlign:'right', color:t.textMuted}}>{(totals.expense > 0 ? ((c.value/totals.expense)*100).toFixed(1) : '0.0')}%</td>
              </tr>
            ))}
            {categoriesData.expense.length===0&&<tr><td colSpan="3" style={{textAlign:'center', padding:20, color:t.textMuted}}>{S().common.noData}</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>}

    {view==='contractors'&&<Card style={{overflowX:'auto'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10, marginBottom:16}}>
        <div style={{fontSize:16, fontWeight:800}}>{S().reports.contractorActivity}</div>
        <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
          {[
            {v:'all',    l:S().reports.contractorFilterAll},
            {v:'clients',l:S().reports.contractorFilterClients},
            {v:'suppliers',l:S().reports.contractorFilterSuppliers},
          ].map(opt => (
            <button key={opt.v} onClick={()=>setContractorFilter(opt.v)} style={{
              padding:'6px 12px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700,
              border:`1px solid ${contractorFilter===opt.v ? t.accent : t.border}`,
              background: contractorFilter===opt.v ? t.accent : 'transparent',
              color: contractorFilter===opt.v ? '#fff' : t.text,
              transition:'all .15s'
            }}>{opt.l}</button>
          ))}
        </div>
      </div>
      <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
        <thead><tr style={{textAlign:'left', background:t.bgInput, color:t.textMuted}}><th style={{padding:'10px 12px'}}>{S().reports.contractorColumn}</th><th style={{padding:'10px 12px', textAlign:'right'}}>{S().reports.revenueColumn}</th><th style={{padding:'10px 12px', textAlign:'right'}}>{S().reports.costsColumn}</th><th style={{padding:'10px 12px', textAlign:'right'}}>{S().reports.balanceColumn}</th><th style={{padding:'10px 12px', textAlign:'right'}}>{S().reports.debtBalanceColumn}</th></tr></thead>
        <tbody>
          {contractorAnalysis.map(c => (
            <tr key={c.id} style={{borderBottom:`1px solid ${t.border}`}}>
              <td style={{padding:'10px 12px', fontWeight:600}}>{c.name}</td>
              <td style={{padding:'10px 12px', textAlign:'right', color:t.accentGreen}}>{fmt(c.income)}</td>
              <td style={{padding:'10px 12px', textAlign:'right', color:t.accentRed}}>{fmt(c.expense)}</td>
              <td style={{padding:'10px 12px', textAlign:'right', fontWeight:700}}>{fmt(c.income - c.expense)}</td>
              <td style={{padding:'10px 12px', textAlign:'right', color: c.debt > 0 ? t.accentRed : c.debt < 0 ? t.accentGreen : t.textMuted, fontWeight:700}}>{fmt(c.debt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>}

    {view==='timeliness'&&<Card style={{overflowX:'auto'}}>
      <div style={{fontSize:16, fontWeight:800, marginBottom:8}}>{S().reports.paymentDiscipline}</div>
      <div style={{fontSize:12, color:t.textMuted, marginBottom:10}}>{S().reports.paymentDisciplineHint}</div>
      <div style={{display:'flex', alignItems:'flex-start', gap:8, padding:10, background:t.bgInput, borderRadius:8, marginBottom:16, fontSize:12, color:t.textMuted}}>
        <Info size={14} style={{flexShrink:0, marginTop:2}}/>
        <span>{S().reports.clientsOnlyNote}</span>
      </div>
      <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
        <thead><tr style={{textAlign:'left', background:t.bgInput, color:t.textMuted}}><th style={{padding:'10px 12px'}}>{S().reports.clientColumn}</th><th style={{padding:'10px 12px', textAlign:'right'}}>{S().reports.dealsColumn}</th><th style={{padding:'10px 12px', textAlign:'right'}}>{S().reports.punctualityColumn}</th><th style={{padding:'10px 12px', textAlign:'right'}}>{S().reports.avgDelayColumn}</th></tr></thead>
        <tbody>
          {timelinessAnalysis.map(c => (
            <tr key={c.id} style={{borderBottom:`1px solid ${t.border}`}}>
              <td style={{padding:'10px 12px', fontWeight:600}}>{c.name}</td>
              <td style={{padding:'10px 12px', textAlign:'right'}}>{c.totalDebts}</td>
              <td style={{padding:'10px 12px', textAlign:'right'}}>
                <div style={{display:'flex', alignItems:'center', justifyContent:'flex-end', gap:8}}>
                  <div style={{width:60, height:6, background:t.border, borderRadius:3, overflow:'hidden'}}><div style={{height:'100%', width:`${c.reliability}%`, background:c.reliability>90?t.accentGreen:c.reliability>70?t.accentYellow:t.accentRed}}/></div>
                  <span style={{fontWeight:700}}>{c.reliability.toFixed(0)}%</span>
                </div>
              </td>
              <td style={{padding:'10px 12px', textAlign:'right', color: c.avgDelay > 0 ? t.accentRed : t.accentGreen, fontWeight:700}}>
                {c.avgDelay > 0 ? `+${c.avgDelay.toFixed(1)} ${S().reports.daysShort}` : c.avgDelay < 0 ? `${c.avgDelay.toFixed(1)} ${S().reports.daysShort}` : S().reports.onTime}
              </td>
            </tr>
          ))}
          {timelinessAnalysis.length===0&&<tr><td colSpan="4" style={{textAlign:'center', padding:30, color:t.textMuted}}>{S().reports.noPaymentHistory}</td></tr>}
        </tbody>
      </table>
    </Card>}
    {view==='liquidity'&&<div style={{display:'grid', gridTemplateColumns:cols, gap:18}}>
      <Card>
        <div style={{fontSize:16, fontWeight:800, marginBottom:16}}>{S().reports.liquidityPosition}</div>
        <div style={{display:'grid', gap:12}}>
          <div style={{display:'flex', justifyContent:'space-between', padding:12, background:t.bgInput, borderRadius:10}}>
            <span>{S().reports.cashOnAccounts}</span>
            <span style={{fontWeight:700, color:t.accent}}>{fmt(liquidityData.cash)}</span>
          </div>
          <div style={{display:'flex', justifyContent:'space-between', padding:12, background:t.bgInput, borderRadius:10}}>
            <span>{S().reports.receivablesLabel}</span>
            <span style={{fontWeight:700, color:t.accentGreen}}>+ {fmt(liquidityData.receivables)}</span>
          </div>
          <div style={{display:'flex', justifyContent:'space-between', padding:12, background:t.bgInput, borderRadius:10}}>
            <span>{S().reports.payablesLabel}</span>
            <span style={{fontWeight:700, color:t.accentRed}}>- {fmt(liquidityData.payables)}</span>
          </div>
          <div style={{display:'flex', justifyContent:'space-between', padding:16, background:`${t.accent}15`, borderRadius:10, marginTop:10, border:`2px solid ${t.accent}`}}>
            <span style={{fontSize:15, fontWeight:800}}>{S().reports.totalLiquidity}</span>
            <span style={{fontSize:18, fontWeight:900, color:t.accent}}>{fmt(liquidityData.total)}</span>
          </div>
        </div>
        <div style={{marginTop:20, fontSize:12, color:t.textMuted, background:t.bgInput, padding:10, borderRadius:8}}>
          <Info size={14} style={{verticalAlign:'middle', marginRight:6}}/>
          {S().reports.liquidityHint}
        </div>
      </Card>
      <Card>
        <div style={{fontSize:15,fontWeight:700,marginBottom:14}}>{S().reports.capitalStructure}</div>
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie 
              data={[
                {name:S().reports.cashName, value:liquidityData.cash, color:t.accent},
                {name:S().reports.receivablesName, value:liquidityData.receivables, color:t.accentGreen}
              ]} 
              cx="50%" cy="50%" outerRadius={80} innerRadius={40} dataKey="value" stroke={t.bgCard} strokeWidth={2}
            >
              <Cell fill={t.accent} />
              <Cell fill={t.accentGreen} />
            </Pie>
            <Tooltip contentStyle={tooltipStyle} formatter={v=>fmt(v)}/>
            <Legend wrapperStyle={{fontSize:11}}/>
          </PieChart>
        </ResponsiveContainer>
      </Card>
    </div>}
  </div>;
}